import {chain, debounce} from 'lodash'
import React, {useCallback, useEffect, useReducer, useState} from 'react'
import http from '../http'
import {Customer, CustomerAddress, CustomerData, OrderResponse} from '../OrderSummary/types'
import OrderReducer from './OrderReducer'
import type {ExchangeItem, OrderAction, OrderState} from './types'
import {useHistory, useLocation} from 'react-router-dom'
import queryString from 'query-string'
import {v4} from 'uuid'
import {addBusinessDays, format} from "date-fns";

type OrderContextState = {
    order: OrderState,
    dispatch: React.Dispatch<OrderAction>,
    orderType: 'Return' | 'Exchange',
    setOrderType: React.Dispatch<'Return' | 'Exchange'>
    pickup: string,
    setPickup: React.Dispatch<string>,
    shipTo: string,
    setShipTo: React.Dispatch<string>,
    billTo: string,
    simulate?: OrderSimulationData,
    loadingSimulate: boolean,
    errorOnSimulate: boolean,
    activeStep: number,
    setActiveStep: React.Dispatch<number>,
    addresses: CustomerAddress[],
    customer: Customer,
    ready: boolean,
    readyFailed: boolean,
    session: string,
    setSession: React.Dispatch<string>,
    initializeOrder: () => void,
    purchases: ExchangeItem[],
    setPurchases: React.Dispatch<ExchangeItem[]>,
    returnsEnabled: boolean,
    exchangesEnabled: boolean,
    orderConfirmationTranslationKey: string
    warehouseAddress?: string,
    pickupDate: Date
    setPickupDate: React.Dispatch<Date>
    dropOffDate: Date | null,
    setDropOffDate: React.Dispatch<Date | null>
}

export type RequestLineItem = {
    material: string,
    batch: string,
    poRef?: string,
    invoiceNumber?: string,
    quantity: number,
    lineItemNum: number,
    description?: string,
    reason: string
}

export type ResponseLineItem = {
    material: string,
    unitPrice: number,
    quantity: number,
    lineItemNum: number,
    netAmount: number
}

export type TemporaryOutOfStockItem = {
    material: string,
    quantity: number,
    confirmQuantity: number,
    estimatedDeliveryDate: string
}

export type OrderSimulationData = {
    currency?: string,
    exchanges: (ResponseLineItem & RequestLineItem)[],
    returns: (ResponseLineItem & RequestLineItem)[],
    purchases: (ResponseLineItem & RequestLineItem)[],
    response: object
    request: string,
    temporaryOutOfStock: TemporaryOutOfStockItem[],
    returnSubtotal?: number
    returnTaxAmount?: number
    returnRefundAmount?: number
    returnShippingCost?: number
    returnCreditAmount?: number
    replacementSubtotal?: number
    replacementTax?: number
    replacementExchangeAmount?: number
    replacementShippingCost?: number
    replacementTotal?: number
    grandTotal?: number
}

type OrderPostRequest = { returns: RequestLineItem[], exchanges: RequestLineItem[], purchases: RequestLineItem[] }

export const makeOrder = (order: OrderState, orderType: 'Return' | 'Exchange', purchases: ExchangeItem[]): OrderPostRequest => {
    let rindex = 0

    const makeReturn = (): RequestLineItem[] => chain(order)
        .map((o) => chain(o.action)
            .map((a) => ({
                material: o.material,
                batch: o.batch,
                invoiceNumber: a.invoiceNumber,
                invoiceLineItem: a.invoiceLineItem,
                poRef: o.poref,
                quantity: a.quantity,
                lineItemNum: (++rindex) * 10,
                description: o.description,
                reason: o.reason
            }))
            .value())
        .flatten()
        .value()

    let eindex = 0

    const returns: RequestLineItem[] = orderType === 'Return' ? makeReturn() : []
    const exchanges: RequestLineItem[] = orderType === 'Exchange' ? makeReturn() : []
    const p: RequestLineItem[] = chain(purchases)
        .map((e) => ({
            material: e.productId as string,
            batch: '',
            quantity: e.quantity,
            lineItemNum: (++eindex) * 10,
            description: e.shortDescription,
            reason: ''
        }))
        .value()

    return {returns, exchanges, purchases: p}
}

export const OrderContext = React.createContext<OrderContextState>({} as OrderContextState)

const getStorageValue = (key: string) => JSON.parse(sessionStorage.getItem('exchangeOrder') || '{}')[key]

export const OrderContextProvider: React.FC = ({children}) => {
    const {pathname, hash} = useLocation()
    const history = useHistory()
    const [session, setSession] = useState(getStorageValue('session') || v4())
    const [order, dispatch] = useReducer(OrderReducer, getStorageValue('orderState'))
    const [pickup, setPickup] = useState<string>(getStorageValue('pickup'))
    const [shipTo, setShipTo] = useState<string>(getStorageValue('shipTo'))
    const [billTo, setBillTo] = useState<string>(getStorageValue('billTo'))
    const [orderType, setOrderType] = useState<'Return' | 'Exchange'>(getStorageValue('orderType') || 'Exchange')
    const [activeStep, setActiveStep] = useState<number>(pathname === '/return' ? parseInt(queryString.parse(hash)['step'] as string) || 0 : 0)
    const [addresses, setAddresses] = useState<CustomerAddress[]>([])
    const [simulate, setSimulate] = useState<OrderSimulationData>({} as OrderSimulationData)
    const [loadingSimulate, setLoadingSimulate] = useState<boolean>(true)
    const [errorOnSimulate, setErrorOnSimulate] = useState<boolean>(false)
    const [customer, setCustomer] = useState<Customer>({} as Customer)
    const [ready, setReady] = useState(false)
    const [readyFailed, setReadyFailed] = useState(false)
    const [purchases, setPurchases] = useState<ExchangeItem[]>(getStorageValue('purchases') || [])
    const [returnsEnabled, setReturnsEnabled] = useState<boolean>(false)
    const [exchangesEnabled, setExchangesEnabled] = useState<boolean>(false)
    const [orderConfirmationTranslationKey, setOrderConfirmationTranslationKey] = useState<string>('received_order')
    const [warehouseAddress, setWarehouseAddress] = useState<string>()
    const [pickupDate, setPickupDate] = useState<Date>(addBusinessDays(new Date(), 1))
    const [dropOffDate, setDropOffDate] = useState<Date | null>(addBusinessDays(new Date(), 1))

    useEffect(() => {
        setDropOffDate(addBusinessDays(pickupDate, 1))
    }, [pickupDate]);

    const initializeOrder = useCallback(() => {
        setOrderType('Exchange')
        setSession(v4())
        setShipTo(addresses.find(a => a.customer === customer.shipToParty)?.customer || addresses[0]?.customer)
        setPickup(addresses.find(a => a.customer === customer.shipToParty)?.customer || addresses[0]?.customer)
        setBillTo(customer.billToParty)
        setActiveStep(0)
        setPurchases([])
        dispatch({type: 'CancelOrder'})
    }, [addresses, customer.billToParty, customer.shipToParty])

    useEffect(() => {
        if (pathname !== '/return') return
        const step = queryString.parse(window.location.hash)['step'] as string || '0'
        if (parseInt(step) !== activeStep) setActiveStep(parseInt(step))
    }, [pathname, hash, activeStep, setActiveStep])

    useEffect(() => {
        sessionStorage.setItem('exchangeOrder', JSON.stringify({
            orderState: order,
            orderType,
            pickup,
            shipTo,
            billTo,
            session,
            purchases
        }))
    }, [order, orderType, pickup, shipTo, billTo, session, purchases])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const doSimulate = useCallback(debounce((o: OrderPostRequest, st: string, pu: string, bt: string, pd: Date) => {
        setLoadingSimulate(true)
        setErrorOnSimulate(false)

        http.post<OrderResponse>('/api/OrderSimulate', {
            ...o,
            shipTo: st,
            pickup: pu,
            billTo: bt,
            customer: {
                shippingMethod: customer.shippingMethod,
                division: customer.division,
                distributionChannel: customer.distributionChannel,
                paymentTerm: customer.paymentTerm
            },
            pickupDate: pu !== 'NoPickup' ? format(pd, 'yyyy-MM-dd') : undefined,
        })
            .then(({data}) => {
                const sim: OrderSimulationData = {
                    ...data,
                    returns: data.returns.map(p => ({...p, ...(o.returns.find(m => m.lineItemNum === p.lineItemNum) as RequestLineItem)})),
                    exchanges: data.exchanges.map(p => ({...p, ...(o.exchanges.find(m => m.lineItemNum === p.lineItemNum) as RequestLineItem)})),
                    purchases: data.purchases.map(p => ({...p, ...(o.purchases.find(m => m.lineItemNum === p.lineItemNum) as RequestLineItem)})),
                }

                setSimulate(sim)
            })
            .catch(() => setErrorOnSimulate(true))
            .finally(() => setLoadingSimulate(false))
    }, 500), [customer, pickupDate])

    useEffect(() => {
        if (!shipTo || !pickup || !customer.paymentTerm || !customer.division || !customer.distributionChannel || !customer.shippingMethod) return

        const o = makeOrder(order, orderType, purchases)

        if ((orderType === 'Return' && !o.returns.length) || (orderType === 'Exchange' && (!o.exchanges.length || !o.purchases.length))) return

        doSimulate(o, shipTo, pickup, billTo, pickupDate)
    }, [order, shipTo, pickup, orderType, billTo, doSimulate, purchases, customer, pickupDate])

    useEffect(() => {
        http.get<CustomerData>('api/Customer')
            .then(({data}) => {
                const addrs = data.partnerAddress.filter(a => a.partner === 'Ship-ToParty')
                setAddresses(addrs)
                setCustomer(data.customerDetails)

                const defaultAddress = !!data.customerDetails.shipToParty && !!addrs.find(a => a.customer === data.customerDetails.shipToParty)
                    ? data.customerDetails.shipToParty
                    : addrs[0]?.customer

                setShipTo(defaultAddress)
                setPickup(defaultAddress)
                setBillTo(data.customerDetails.billToParty)
                setReturnsEnabled(data.customerDetails.returnsEnabled || false)
                setExchangesEnabled(data.customerDetails.exchangesEnabled || false)
                setOrderConfirmationTranslationKey(data.customerDetails.orderConfirmationTranslationKey || 'received_order')
                setWarehouseAddress(data.customerDetails.warehouseAddress)
            })
            .catch(console.log)
    }, [])

    useEffect(() => {
        http.post('api/pcat')
            .then(() => {
                setReady(true);
                setReadyFailed(false);
            })
            .catch(() => {
                setReady(false);
                setReadyFailed(true);
            })
    }, [])

    return <OrderContext.Provider children={children} value={{
        order,
        dispatch,
        pickup,
        setPickup,
        shipTo,
        setShipTo,
        billTo,
        simulate,
        loadingSimulate,
        errorOnSimulate,
        orderType,
        setOrderType: (ot: 'Return' | 'Exchange') => {
            setOrderType(ot)
            dispatch({type: 'CancelOrder'})
        },
        activeStep,
        setActiveStep: (step: number) => {
            setActiveStep(step)
            if (window.location.pathname === '/return') history.push(`${window.location.pathname}#${queryString.stringify({step})}`)
        },
        addresses,
        customer,
        ready,
        readyFailed,
        session,
        setSession,
        initializeOrder,
        purchases,
        setPurchases,
        returnsEnabled,
        exchangesEnabled,
        orderConfirmationTranslationKey,
        warehouseAddress,
        pickupDate,
        setPickupDate,
        dropOffDate,
        setDropOffDate
    }}/>
}
